import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Toaster } from 'react-hot-toast';

import { store, persistor } from '@/store';
import { createAppTheme } from '@/utils/theme';

// Layouts
import AuthLayout from '@/layouts/AuthLayout';
import MainLayout from '@/layouts/MainLayout';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';

// Public Pages
import ValidarComprovantePage from '@/pages/ValidarComprovantePage';
import AprovarPage from '@/pages/aprovar/AprovarPage';
import LandingPage from '@/pages/LandingPage';
import RootGate from '@/components/RootGate';

// Main Pages
import DashboardPage from '@/pages/DashboardPage';
import FinancialBIPage from '@/pages/FinancialBIPage';
import StatementsPage from '@/pages/StatementsPage';
import ReportsPage from '@/pages/ReportsPage';
import ProfilePage from '@/pages/ProfilePage';

// PIX Pages
import PixKeysPage from '@/pages/pix/PixKeysPage';
import PixTransferPage from '@/pages/pix/PixTransferPage';
import PixQRCodePage from '@/pages/pix/PixQRCodePage';
import PixHistoryPage from '@/pages/pix/PixHistoryPage';
import PixCobVPage from '@/pages/pix/PixCobVPage';
import PixWebhooksPage from '@/pages/pix/PixWebhooksPage';
import PixEnviarPage from '@/pages/pix/PixEnviarPage';
import PixRecebidosPage from '@/pages/pix/PixRecebidosPage';
import PixCobrancaPage from '@/pages/pix/PixCobrancaPage';

// Conta Corrente Pages
import ContaSaldoPage from '@/pages/conta/ContaSaldoPage';
import ContaExtratoPage from '@/pages/conta/ContaExtratoPage';

// Boleto Pages
import BoletoNewPage from '@/pages/boletos/BoletoNewPage';
import BoletoListPage from '@/pages/boletos/BoletoListPage';
import BoletoDetailPage from '@/pages/boletos/BoletoDetailPage';
import BoletoReportsPage from '@/pages/boletos/BoletoReportsPage';
import BoletoMovimentacoesPage from '@/pages/boletos/BoletoMovimentacoesPage';
import BoletoMovimentacaoViewerPage from '@/pages/boletos/BoletoMovimentacaoViewerPage';

// Pagamentos Pages
import PagarBoletoPage from '@/pages/pagamentos/PagarBoletoPage';
import PagamentosDDAPage from '@/pages/pagamentos/PagamentosDDAPage';
import SolicitacoesPagamentoPage from '@/pages/pagamentos/SolicitacoesPagamentoPage';

// Transfer Pages
import TransferNewPage from '@/pages/transfers/TransferNewPage';
import TransferTEDPage from '@/pages/transfers/TransferTEDPage';
import TransferInternalPage from '@/pages/transfers/TransferInternalPage';
import TransferHistoryPage from '@/pages/transfers/TransferHistoryPage';

// Admin Pages
import UsersPage from '@/pages/admin/UsersPage';
import CompanyPage from '@/pages/admin/CompanyPage';
import SecurityPage from '@/pages/admin/SecurityPage';

// Settings Pages
import SettingsPage from '@/pages/settings/SettingsPage';
import SicoobSettingsPage from '@/pages/settings/SicoobSettingsPage';
import EfiSettingsPage from '@/pages/settings/EfiSettingsPage';
import EfiCobrancasPage from '@/pages/efi/EfiCobrancasPage';
import BankAccountsPage from '@/pages/settings/BankAccountsPage';
import WebhooksSettingsPage from '@/pages/settings/WebhooksSettingsPage';
import WebhookLogsPage from '@/pages/settings/WebhookLogsPage';
import CertificateConverterPage from '@/pages/settings/CertificateConverterPage';
import ApiDocsPage from '@/pages/settings/ApiDocsPage';
import AppsPage from '@/pages/settings/AppsPage';
import AprovacoesSettingsPage from '@/pages/settings/AprovacoesSettingsPage';

// Components
import LoadingScreen from '@/components/common/LoadingScreen';

// Tema Mantis
const theme = createAppTheme('light');

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingScreen />} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Router>
              <Routes>
                {/* Rotas de autenticação */}
                <Route path="/login" element={
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                } />

                {/* Validação pública de comprovante (QR Code) */}
                <Route path="/validar/:id" element={<ValidarComprovantePage />} />

                {/* Painel do aprovador por link pessoal (token no #, código no WhatsApp a cada 24 h) */}
                <Route path="/aprovar" element={<AprovarPage />} />

                {/* Landing page pública de vendas (raiz + /sobre) */}
                <Route path="/" element={<RootGate />} />
                <Route path="/sobre" element={<LandingPage />} />

                {/* Rotas protegidas com MainLayout */}
                <Route element={<MainLayout />}>
                  {/* Dashboard */}
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="bi" element={<FinancialBIPage />} />

                  {/* Conta Corrente */}
                  <Route path="conta/saldo" element={<ContaSaldoPage />} />
                  <Route path="conta/extrato" element={<ContaExtratoPage />} />

                  {/* PIX — neutros (/bank/*, qualquer provedor) */}
                  <Route path="pix/recebidos" element={<PixRecebidosPage />} />
                  <Route path="pix/cobranca" element={<PixCobrancaPage />} />

                  {/* PIX — Sicoob */}
                  <Route path="pix/enviar" element={<PixEnviarPage />} />
                  <Route path="pix/keys" element={<PixKeysPage />} />
                  <Route path="pix/transfer" element={<PixTransferPage />} />
                  <Route path="pix/qrcode" element={<PixQRCodePage />} />
                  <Route path="pix/cobv" element={<PixCobVPage />} />
                  <Route path="pix/history" element={<PixHistoryPage />} />
                  <Route path="pix/webhooks" element={<PixWebhooksPage />} />

                  {/* Boletos */}
                  <Route path="boletos/new" element={<BoletoNewPage />} />
                  <Route path="boletos/list" element={<BoletoListPage />} />
                  <Route path="boletos/:nossoNumero" element={<BoletoDetailPage />} />
                  <Route path="boletos/reports" element={<BoletoReportsPage />} />
                  <Route path="boletos/movimentacoes" element={<BoletoMovimentacoesPage />} />
                  <Route path="boletos/movimentacoes/viewer" element={<BoletoMovimentacaoViewerPage />} />

                  {/* Pagamentos */}
                  <Route path="pagamentos/pagar" element={<PagarBoletoPage />} />
                  <Route path="pagamentos/dda" element={<PagamentosDDAPage />} />
                  <Route path="pagamentos/solicitacoes" element={<SolicitacoesPagamentoPage />} />

                  {/* Transferências */}
                  <Route path="transfers/new" element={<TransferNewPage />} />
                  <Route path="transfers/ted" element={<TransferTEDPage />} />
                  <Route path="transfers/internal" element={<TransferInternalPage />} />
                  <Route path="transfers/history" element={<TransferHistoryPage />} />

                  {/* Extratos e Relatórios */}
                  <Route path="statements" element={<StatementsPage />} />
                  <Route path="reports" element={<ReportsPage />} />

                  {/* Admin */}
                  <Route path="admin/users" element={<UsersPage />} />
                  <Route path="admin/company" element={<CompanyPage />} />
                  <Route path="admin/security" element={<SecurityPage />} />

                  {/* Configurações e Perfil */}
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="settings/contas" element={<BankAccountsPage />} />
                  <Route path="settings/sicoob" element={<SicoobSettingsPage />} />
                  <Route path="settings/efi" element={<EfiSettingsPage />} />
                  <Route path="efi/cobrancas" element={<EfiCobrancasPage />} />
                  <Route path="settings/webhooks" element={<WebhooksSettingsPage />} />
                  <Route path="settings/webhook-logs" element={<WebhookLogsPage />} />
                  <Route path="settings/certificate-converter" element={<CertificateConverterPage />} />
                  <Route path="settings/api-docs" element={<ApiDocsPage />} />
                  <Route path="settings/apps" element={<AppsPage />} />
                  <Route path="settings/aprovacoes" element={<AprovacoesSettingsPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
            <Toaster position="top-right" />
          </LocalizationProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
